import { createTheme, alpha } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
  }
  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
  }
}

// Theme configuration based on design system
const colors = {
  bgPrimary: '#0f172a',
  bgSecondary: '#1e293b',
  bgTertiary: '#334155',
  textPrimary: '#f8fafc',
  textSecondary: '#94a3b8',
  brandPrimary: '#22c55e',
  brandHover: '#16a34a',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  borderDefault: '#334155',
  borderFocus: '#6366f1',
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.brandPrimary,
      light: '#4ade80',
      dark: colors.brandHover,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: colors.borderFocus,
      light: '#818cf8',
      dark: '#4f46e5',
      contrastText: '#FFFFFF',
    },
    neutral: {
      main: colors.textSecondary,
      light: '#cbd5e1',
      dark: '#64748b',
      contrastText: '#FFFFFF',
    },
    background: {
      default: colors.bgPrimary,
      paper: colors.bgSecondary,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
    divider: colors.borderDefault,
    success: {
      main: colors.success,
      light: '#4ade80',
      dark: '#16a34a',
    },
    warning: {
      main: colors.warning,
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: colors.error,
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: colors.info,
      light: '#60a5fa',
      dark: '#2563eb',
    },
    action: {
      active: colors.textPrimary,
      hover: alpha(colors.textPrimary, 0.08),
      selected: alpha(colors.brandPrimary, 0.16),
      disabled: alpha(colors.textSecondary, 0.38),
      disabledBackground: alpha(colors.textSecondary, 0.12),
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: colors.textPrimary,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      color: colors.textPrimary,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: colors.textPrimary,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.textPrimary,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.textPrimary,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: colors.textPrimary,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: colors.textPrimary,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.57,
      color: colors.textSecondary,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
    caption: {
      color: colors.textSecondary,
    },
    overline: {
      color: colors.textSecondary,
    },
  },
  shape: {
    borderRadius: 8, // md = 0.5rem
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.bgPrimary,
          scrollbarColor: `${colors.bgTertiary} ${colors.bgSecondary}`,
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: colors.bgSecondary,
          },
          '&::-webkit-scrollbar-thumb': {
            background: colors.bgTertiary,
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: colors.textSecondary,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontWeight: 600,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha(colors.brandPrimary, 0.35)}`,
          },
        },
        containedPrimary: {
          backgroundColor: colors.brandPrimary,
          '&:hover': {
            backgroundColor: colors.brandHover,
          },
        },
        outlined: {
          borderColor: colors.borderDefault,
          '&:hover': {
            borderColor: colors.textSecondary,
            backgroundColor: alpha(colors.textPrimary, 0.04),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(colors.textPrimary, 0.08),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: colors.bgSecondary,
          boxShadow: 'none',
          border: `1px solid ${colors.borderDefault}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.bgSecondary,
        },
        elevation1: {
          boxShadow: `0 1px 3px ${alpha('#000', 0.3)}`,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: colors.bgTertiary,
            '& fieldset': {
              borderColor: colors.borderDefault,
            },
            '&:hover fieldset': {
              borderColor: colors.textSecondary,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.borderFocus,
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.textSecondary,
          },
          '& .MuiInputBase-input': {
            color: colors.textPrimary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.borderDefault,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.textSecondary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.borderFocus,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: colors.bgTertiary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderColor: colors.borderDefault,
        },
        outlined: {
          borderColor: colors.borderDefault,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: colors.bgSecondary,
          borderBottom: `1px solid ${colors.borderDefault}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.bgSecondary,
          borderRight: `1px solid ${colors.borderDefault}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: alpha(colors.textPrimary, 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(colors.brandPrimary, 0.16),
            '&:hover': {
              backgroundColor: alpha(colors.brandPrimary, 0.24),
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.borderDefault,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: colors.borderDefault,
        },
        head: {
          backgroundColor: colors.bgTertiary,
          color: colors.textPrimary,
          fontWeight: 600,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(colors.textPrimary, 0.04),
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: alpha(colors.success, 0.12),
          color: colors.success,
        },
        standardError: {
          backgroundColor: alpha(colors.error, 0.12),
          color: colors.error,
        },
        standardWarning: {
          backgroundColor: alpha(colors.warning, 0.12),
          color: colors.warning,
        },
        standardInfo: {
          backgroundColor: alpha(colors.info, 0.12),
          color: colors.info,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.borderDefault}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.borderDefault}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.bgTertiary,
          color: colors.textPrimary,
          border: `1px solid ${colors.borderDefault}`,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(colors.textPrimary, 0.08),
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            backgroundColor: colors.bgTertiary,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colors.brandPrimary,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: colors.textSecondary,
          '&.Mui-selected': {
            color: colors.brandPrimary,
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: colors.brandPrimary,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: colors.bgTertiary,
        },
        bar: {
          backgroundColor: colors.brandPrimary,
        },
      },
    },
  },
});
